import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

/*
  One-time dismissible dashboard install banner.
  Shown only if:
    - the PWA is NOT already installed (best-effort detection via display-mode),
    - the browser supports beforeinstallprompt (so we have something to prompt with),
    - the user hasn't previously dismissed it on this device.
*/
const DISMISS_KEY = "rcs-dashboard-install-dismissed-v1";

function alreadyInstalled() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

export default function DashboardInstallBanner() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (alreadyInstalled()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch { /* localStorage blocked — show anyway, it just won't persist */ }
    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice?.outcome === "accepted") setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="flex items-center gap-3 p-4 rounded-lg border border-[#0B5FFF]/20 bg-[#EFF4FF]/40 mb-4"
      data-testid="dashboard-install-banner"
    >
      <div className="w-10 h-10 rounded-md bg-[#0B5FFF] text-white flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-sm text-[#111827]">Install RapidCare SA on your device for offline access.</div>
        <div className="text-xs text-slate-600 mt-0.5">One-tap access from your home screen. Core protocols and calculators work without signal.</div>
      </div>
      <Button onClick={install} size="sm" className="bg-[#0B5FFF] hover:bg-[#0A54E0] h-9 text-xs px-4" data-testid="dashboard-install-accept">
        Install
      </Button>
      <button
        onClick={dismiss}
        className="text-slate-500 hover:text-slate-900 p-1"
        aria-label="Dismiss"
        data-testid="dashboard-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
